/**
 * Projects root — filter/table helpers, API response mapping (projects, work items, attachments).
 */
import { normalizeProjectFromApi, normalizeWorkItemFromApi } from "./projectManagementNormalizers";
import { getListFromApiResponse } from "../utils/apiListMappers";
import { formatDate } from "../../utils/dateUtils";
import { parseEpicProgressFromWorkItemsResponse } from "../Issues/epicChildIssuesUtils";

export { getListFromApiResponse };

/** Single value from filter field (multiselect: first element). */
export function getFilterSingleValue(field) {
  if (field == null) return "";
  return Array.isArray(field) ? (field[0] ?? "") : field;
}

function getProjectStatusColor(status) {
  const normalized = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  switch (normalized) {
    case "active":
      return "bg-badge-success";
    case "completed":
      return "bg-badge-info";
    case "on_hold":
    case "on-hold":
      return "bg-badge-warning";
    case "archived":
      return "bg-badge-secondary";
    default:
      return "bg-badge-secondary";
  }
}

/**
 * API often returns `ACTIVE`, `ON_HOLD`, etc. List UI shows title-style labels.
 */
export function formatProjectStatusLabel(status) {
  const key = String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  const labels = {
    ACTIVE: "Active",
    COMPLETED: "Completed",
    ON_HOLD: "On Hold",
    ARCHIVED: "Archived",
  };
  if (labels[key]) return labels[key];
  if (!status || String(status).trim() === "") return "Active";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Table row: display strings and badge class for Project list. */
export function mapProjectToTableRow(project) {
  const dateValue = project?.receivedDate ?? project?.startDate;
  const rawStatus = project?.status ?? "ACTIVE";
  return {
    clientName: project?.clientName ?? project?.client?.name ?? "N/A",
    projectName: project?.projectName ?? project?.name ?? "N/A",
    startDateDisplay: formatDate(dateValue),
    status: formatProjectStatusLabel(rawStatus),
    statusColorClass: getProjectStatusColor(rawStatus),
  };
}

export function getClientNamesFromList(clientsList) {
  const list = getListFromApiResponse(clientsList);
  return [...new Set(list.map((c) => c.clientName ?? c.name).filter(Boolean))];
}

export function getProjectNamesFromList(projectsList) {
  const list = getListFromApiResponse(projectsList);
  return [...new Set(list.map((p) => p.projectName ?? p.name).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
  );
}

/**
 * Label for project filter / dropdown: `Client - Project` when both exist.
 * @param {object} project
 * @returns {string}
 */
export function formatProjectWithClientLabel(project) {
  if (project == null || typeof project !== "object") return "";
  const projectName = String(project.projectName ?? project.name ?? "").trim();
  const clientName = String(
    project.clientName ?? project.client?.name ?? project.client?.clientName ?? ""
  ).trim();
  if (!projectName && !clientName) return "";
  if (!clientName) return projectName;
  if (!projectName) return clientName;
  return `${clientName} - ${projectName}`;
}

/**
 * Project filter options from GET all-projects (or any projects list payload).
 * @param {object|array|null} projectsData
 * @param {{ valueMode?: "id" | "name" }} [options]
 * @returns {Array<{ label: string, value: string }>}
 */
export function mapProjectsToFilterOptions(projectsData, { valueMode = "id" } = {}) {
  const list = getListFromApiResponse(projectsData);
  const options = [];
  for (const project of list) {
    const id = project?.id ?? project?.projectId;
    const name = String(project?.projectName ?? project?.name ?? "").trim();
    const label = formatProjectWithClientLabel(project);
    if (!label) continue;
    if (valueMode === "name") {
      if (!name) continue;
      options.push({ label, value: name });
      continue;
    }
    if (id == null || String(id).trim() === "") continue;
    options.push({ label, value: String(id) });
  }
  return options.sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" })
  );
}

export function getClientNamesFromProjectsList(projectsResponse) {
  const list = getListFromApiResponse(projectsResponse);
  return [...new Set(list.map((p) => p.clientName ?? p.name).filter(Boolean))];
}

// --- API: paged list shape shared by projects & work items ---

function extractPagedList(raw) {
  if (raw == null) return { list: [], meta: null };
  // Common: { data: { data: [], meta } } (work items, backlog)
  // Also: { data: [] }, top-level { list: [] } (legacy mocks)
  const list =
    raw?.data?.data ??
    (Array.isArray(raw?.data) ? raw.data : null) ??
    (Array.isArray(raw?.list) ? raw.list : null) ??
    raw?.data;
  const meta = raw?.data?.meta ?? raw?.meta ?? null;
  const safeList = Array.isArray(list) ? list : [];
  return { list: safeList, meta };
}

export function mapProjectsListResponse(raw) {
  const { list, meta } = extractPagedList(raw);
  return { list: list.map(normalizeProjectFromApi), meta };
}

export function mapWorkItemsListResponse(raw) {
  const { list, meta } = extractPagedList(raw);
  const listSafe = Array.isArray(list)
    ? list.filter((item) => item != null && typeof item === "object")
    : [];
  return {
    list: listSafe
      .map((item) => {
        try {
          return normalizeWorkItemFromApi(item);
        } catch {
          return null;
        }
      })
      .filter(Boolean),
    meta,
    epicProgress: parseEpicProgressFromWorkItemsResponse(raw),
  };
}

export function mapWorkItemResponse(raw) {
  return normalizeWorkItemFromApi(raw);
}

export function mapWorkItemAttachmentsListResponse(raw) {
  return raw?.data;
}

export function mapWorkItemAttachmentUrlResponse(raw) {
  return raw?.data?.url;
}

/** Thunks return `response.data` (API body). Unwrap nested `{ data: entity }` for work items. */
export function unwrapWorkItemApiPayload(apiPayload) {
  if (apiPayload == null) return apiPayload;
  return apiPayload.data !== undefined ? apiPayload.data : apiPayload;
}

export function mapWorkItemFromApiPayload(apiPayload) {
  return mapWorkItemResponse(unwrapWorkItemApiPayload(apiPayload));
}

/** Primary instant for a work-item comment (API `timestamp`, legacy `created_at`). */
export function resolveWorkItemCommentTimestamp(comment) {
  if (!comment || typeof comment !== "object") return null;
  const raw =
    comment.timestamp ??
    comment.created_at ??
    comment.createdAt ??
    comment.updated_at ??
    comment.updatedAt ??
    null;
  if (raw == null || String(raw).trim() === "") return null;
  return raw;
}

/** Comments / history / time logs — API body from GET (thunk payload). */
function getWorkItemCommentTimestampMs(comment) {
  const raw = resolveWorkItemCommentTimestamp(comment);
  if (raw == null) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function sortWorkItemCommentsNewestFirst(comments) {
  if (!Array.isArray(comments) || comments.length <= 1) return comments ?? [];
  return [...comments].sort(
    (a, b) => getWorkItemCommentTimestampMs(b) - getWorkItemCommentTimestampMs(a)
  );
}

export function parseWorkItemCommentsPayload(apiPayload) {
  const raw = apiPayload?.data ?? apiPayload;
  const dataWrapper = raw?.data ?? raw;
  const rawList = Array.isArray(dataWrapper)
    ? dataWrapper
    : Array.isArray(dataWrapper?.data)
      ? dataWrapper.data
      : [];
  const meta = dataWrapper?.meta ?? raw?.meta ?? null;
  return { list: sortWorkItemCommentsNewestFirst(rawList), meta };
}

export function parseWorkItemHistoryPayload(apiPayload) {
  const raw = apiPayload?.data ?? apiPayload;
  const data = raw?.data ?? raw;
  const list = Array.isArray(data) ? data : (data?.data ?? []);
  return Array.isArray(list) ? list : [];
}

export function parseWorkItemTimeLogsPayload(apiPayload) {
  const raw = apiPayload?.data ?? apiPayload;
  const dataWrapper = raw?.data ?? raw;
  let list = [];
  if (Array.isArray(dataWrapper)) {
    list = dataWrapper;
  } else if (Array.isArray(dataWrapper?.data)) {
    list = dataWrapper.data;
  } else if (Array.isArray(dataWrapper?.list)) {
    list = dataWrapper.list;
  } else if (Array.isArray(raw?.list)) {
    list = raw.list;
  }
  const meta = dataWrapper?.meta ?? raw?.meta ?? null;
  return { list, meta };
}

export function normalizeMultipartUploadPayload(apiPayload) {
  const raw = apiPayload?.data ?? apiPayload;
  return raw?.data ?? raw;
}

export function unwrapPostMutationPayload(apiPayload) {
  return apiPayload?.data?.data ?? apiPayload?.data ?? apiPayload;
}
